import type { EChartsOption } from 'echarts'
import type { CallbackDataParams } from 'echarts/types/dist/shared'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { IBarChartSourceData } from '@/interfaces/api/widget'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { useI18n } from '@/composable/useI18n'
import {
  getSchemaFieldLabel,
  resolveDataFieldUnit,
  resolveWidgetDataFields,
} from '@/utils/sensor-schema-fields'
import type { BarChartResolution } from '@/utils/chart/bar-chart-resolution'
import { indicatorSourceKeyFromWidget, isGroupWidget } from '@/utils/dashboard/widget-source'
import { formatWidgetMetricValue } from '@/utils/dashboard/format-widget-metric'
import { storeToRefs } from 'pinia'

export const BAR_CHART_ACCENT_COLOR = '#80CAEE'

const BAR_SLICE_COLORS = [
  '#80CAEE',
  '#12B76A',
  '#123add',
  '#F79009',
  '#F04438',
  '#7A5AF8',
  '#EE46BC',
  '#15B79E',
  '#6172F3',
]

const MONO_FONT = 'Inter, sans-serif'

export function buildBarChartSourceKey(
  windowPeriod: string,
  sensorId: string,
  dataField: string,
): string {
  return `${windowPeriod}:${sensorId}:${dataField}`
}

function formatBucketLabel(isoTime: string, resolution: BarChartResolution): string {
  const date = new Date(isoTime)

  if (resolution === '1d' || resolution === '1w') {
    return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' })
  }

  if (resolution === '1h') {
    return date.toLocaleString([], {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatTooltipValue(
  value: number | null | undefined,
  dataField: string,
  unit: string,
): string {
  if (value === null || value === undefined) return '-'
  return formatWidgetMetricValue(value, dataField, unit)
}

interface IBarPointMeta {
  value: number | null
  unit: string
  dataField: string
}

export function useBarChartWidget() {
  const store = useCompanyDashboardStore()
  const { barChartSources, indicatorSources } = storeToRefs(store)
  const { t, te } = useI18n()

  const getGroupCategoricalOption = (widget: IWidget): EChartsOption => {
    const dataFields = resolveWidgetDataFields(widget.config)
    const categories: string[] = []
    const values: Array<{ value: number | null; unit: string; dataField: string }> = []

    dataFields.forEach((dataField) => {
      const key = indicatorSourceKeyFromWidget(widget, dataField)
      const source = key ? indicatorSources.value[key] : undefined
      const value = source?.aggregations?.last ?? source?.aggregations?.mean ?? null
      const unit = resolveDataFieldUnit(dataField, source?.unit ?? '')
      categories.push(t(`custom_dashboards.widget_modal.group_fields.${dataField}`))
      values.push({ value, unit, dataField })
    })

    return {
      grid: { top: 16, right: 12, bottom: 48, left: 48 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: MONO_FONT, fontSize: 11 },
        formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
          if (!Array.isArray(params) || params.length === 0) return ''
          const param = params[0]
          const rawData = param.data as IBarPointMeta | number | null
          const value = typeof rawData === 'object' && rawData !== null ? rawData.value : rawData
          const unit = typeof rawData === 'object' && rawData !== null ? rawData.unit : ''
          const dataField =
            typeof rawData === 'object' && rawData !== null ? rawData.dataField : ''
          return `${param.name}: ${formatTooltipValue(
            typeof value === 'number' ? value : null,
            dataField,
            unit,
          )}`
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { color: '#64748B', fontSize: 10, fontFamily: MONO_FONT },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#64748B', fontSize: 10, fontFamily: MONO_FONT },
        splitLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          barCategoryGap: '30%',
          itemStyle: { color: BAR_SLICE_COLORS[0] },
          data: values,
        },
      ],
    }
  }

  const getOption = (widget: IWidget, windowPeriod: string): EChartsOption => {
    if (isGroupWidget(widget)) {
      return getGroupCategoricalOption(widget)
    }

    const sensorId = widget.config.sensor_ids?.[0]
    const dataFields = resolveWidgetDataFields(widget.config)

    const seriesSources: Array<{
      dataField: string
      source: IBarChartSourceData | undefined
      label: string
      unit: string
      color: string
    }> = []

    dataFields.forEach((dataField, index) => {
      const sourceKey = buildBarChartSourceKey(windowPeriod, sensorId, dataField)
      const source = barChartSources.value[sourceKey]
      seriesSources.push({
        dataField,
        source,
        label: getSchemaFieldLabel(dataField, t, te),
        unit: resolveDataFieldUnit(dataField, source?.unit ?? ''),
        color: BAR_SLICE_COLORS[index % BAR_SLICE_COLORS.length],
      })
    })

    const allTimestamps = new Set<string>()
    seriesSources.forEach(({ source }) => {
      source?.points.forEach((point) => {
        if (point.t) allTimestamps.add(point.t)
      })
    })

    const sortedTimestamps = [...allTimestamps].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    )

    const categories = sortedTimestamps.map((timestamp) =>
      formatBucketLabel(timestamp, windowPeriod as BarChartResolution),
    )

    const series = seriesSources.map(({ dataField, source, label, unit, color }) => {
      const pointMap = new Map(
        (source?.points ?? []).map((point) => [point.t, point.v ?? null]),
      )

      return {
        name: label,
        type: 'bar' as const,
        barGap: '10%',
        barCategoryGap: '20%',
        itemStyle: { color },
        data: sortedTimestamps.map((timestamp) => ({
          value: pointMap.get(timestamp) ?? null,
          unit,
          dataField,
        })),
      }
    })

    return {
      grid: { top: 16, right: 12, bottom: 48, left: 48 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: MONO_FONT, fontSize: 11 },
        formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
          if (!Array.isArray(params) || params.length === 0) return ''

          const header = String(params[0].axisValueLabel ?? params[0].name ?? '')
          const lines = params.map((param) => {
            const rawData = param.data as IBarPointMeta | number | null
            const value = typeof rawData === 'object' && rawData !== null ? rawData.value : rawData
            const dataField =
              typeof rawData === 'object' && rawData !== null
                ? rawData.dataField
                : seriesSources[param.seriesIndex ?? 0]?.dataField ?? ''
            const unit =
              typeof rawData === 'object' && rawData !== null
                ? rawData.unit
                : resolveDataFieldUnit(dataField, '')
            const formatted = formatTooltipValue(
              typeof value === 'number' ? value : null,
              dataField,
              unit,
            )
            return `${param.marker ?? ''} ${param.seriesName ?? ''}: ${formatted}`
          })

          return [header, ...lines].join('<br/>')
        },
      },
      legend: {
        bottom: 4,
        left: 'center',
        icon: 'rect',
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: { fontSize: 10, color: '#64748B', fontFamily: MONO_FONT },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { color: '#64748B', fontSize: 10, fontFamily: MONO_FONT },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#64748B', fontSize: 10, fontFamily: MONO_FONT },
        splitLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series,
    }
  }

  return { getOption, ACCENT_COLOR: BAR_CHART_ACCENT_COLOR }
}
